import { useMemo, useRef } from "react";
import { ADVANCED_STAT_CAPTIONS, statRootOf } from "../config/matchStatsCaptions";

// Derives the ACTUAL on-air state of the stats graphic from the broker command
// state that useBrokerSubscription tracks live (initial getCommands +
// subscriptionToCommand over the app's configured Apollo/WS link).
//
//  - actionState[graphicId] → last action on the layer (MATCH_STATS_IN/OUT).
//    This is RELIABLE for IN/OUT.
//  - dataState[graphicId]   → deep-MERGED command data. Because it accumulates
//    across commands, its stat titles can be stale, so identifying WHICH stat
//    from it is only best-effort (the caller prefers a localStorage record of
//    what it put up). Still exposed here as `onAirCaption` for externally-set
//    graphics.

const findMatchStats = (node, depth = 0) => {
  if (!node || typeof node !== "object" || depth > 5) return null;
  if (node.MatchStats && typeof node.MatchStats === "object") return node.MatchStats;
  if (node.StatArray && typeof node.StatArray === "object") return node; // node itself IS MatchStats
  for (const v of Object.values(node)) {
    const found = findMatchStats(v, depth + 1);
    if (found) return found;
  }
  return null;
};
const extractMatchStats = (data) => {
  let d = data;
  if (typeof d === "string") {
    try { d = JSON.parse(d); } catch { return null; }
  }
  return findMatchStats(d);
};

// Titles may differ in case/spacing between the broker and our tiles.
const norm = (s) => String(s ?? "").trim().toLowerCase().replace(/\s+/g, " ");

const matchCaption = (ms, single = [], double = []) => {
  if (!ms) return null;
  const arr = ms.StatArray || {};
  const isDouble = Number(ms.iHowMany) === 2 || !!arr.Stat2;
  if (isDouble) {
    const s1 = norm(arr.Stat1?.strTitle);
    const s2 = norm(arr.Stat2?.strTitle);
    return double.find((d) => norm(d.stat1?.title) === s1 && norm(d.stat2?.title) === s2)?.name || null;
  }
  const s1 = norm(arr.Stat1?.strTitle);
  return single.find((x) => norm(x.title) === s1)?.name || null;
};

/**
 * The gfx_PermScore layer is SHARED — the persistent clock/score ("Main")
 * graphic fires the GENERIC `IN`/`OUT`, cards fire `CARD_*`, MatchStats fires
 * `MATCH_STATS_IN`/`MATCH_STATS_OUT` and each advanced stat its own root. So we
 * key on the stats-specific actions (STAT_ACTION_ROOTS), NOT any `*IN` —
 * otherwise a score/clock update (generic `IN`) would read as stats on air.
 *
 * @returns {{ action: string, matchStatsOnAir: boolean, matchStatsOff: boolean, onAirCaption: string|null }}
 */
export function useOnAirStatsCaption({ actionState, dataState, graphicId = "gfx_PermScore", single = [], double = [] }) {
  // Refs so the tiles (which re-resolve on every DDS tick) don't churn deps.
  const singleRef = useRef(single);
  const doubleRef = useRef(double);
  singleRef.current = single;
  doubleRef.current = double;
  const titlesSig = useMemo(
    () => (single || []).map((s) => s.title).join("|") + "##" + (double || []).map((d) => d.title).join("|"),
    [single, double],
  );

  return useMemo(() => {
    const action = actionState?.[graphicId] || "";
    const onAirRoot = statRootOf(action, "IN");
    const matchStatsOnAir = Boolean(onAirRoot);
    const matchStatsOff = Boolean(statRootOf(action, "OUT"));
    // An advanced stat owns its root, so its caption is known; Single/Double are matched by title.
    const advanced = ADVANCED_STAT_CAPTIONS.find((c) => c.root === onAirRoot);
    const ms = onAirRoot === "MATCH_STATS" ? extractMatchStats(dataState?.[graphicId]) : null;
    const onAirCaption = advanced ? advanced.name : (ms ? matchCaption(ms, singleRef.current, doubleRef.current) : null);
    return { action, matchStatsOnAir, matchStatsOff, onAirCaption };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionState, dataState, graphicId, titlesSig]);
}
